import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  Home,
  Globe,
  Briefcase,
  Calendar,
  Users,
  Layers,
  Building,
  Film,
  Zap,
  GraduationCap,
  Heart,
  Leaf
} from 'lucide-react';
import logo1 from './pieeslogo.png';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRefs = useRef([]);

  // Define dropdown menu items for each header link
  const navLinks = [
    {
      title: 'Home',
      href: '/',
      icon: <Home size={18} className="mr-1" />,
      dropdownItems: [
        { title: 'Introduction', href: '/introduction' },
        { title: 'Tactical Activities', href: '/tactical-activities' },
        { title: 'Immediate Initiatives', href: '/initiatives' },
        { title: 'Leadership', href: '/directors' }
      ]
    },
    {
      title: 'Sector Specific Initiatives',
      href: '/sector',
      icon: <Layers size={18} className="mr-1" />,
      dropdownItems: [
        { title: 'Environment', href: '/environment' },
        { title: 'Tourism', href: '/tourism' },
        { title: 'Energy', href: '/energy' },
        { title: 'Education', href: '/education' },
        { title: 'Agri-Tech', href: '/agri-tech' }
      ]
    },
    {
      title: 'Events & Activities',
      href: '/events',
      icon: <Calendar size={18} className="mr-1" />,
      dropdownItems: [
        { title: 'Events', href: '/events' },
        { title: 'Blogs', href: '/blogs' },
      ]
    },
    // {
    //   title: 'Account',
    //   href: '#sectors',
    //   icon: <User size={18} className="mr-1" />,
    //   dropdownItems: [
    //     { title: 'Login', href: '/login' },
    //     { title: 'Register', href: '/register' },
    //   ]
    // },
    {
      title: 'Join Us',
      href: '/join-us',
      icon: <Heart size={18} className="mr-1" />,
      dropdownItems: [
        { title: 'Strengthen Our Journey', href: '/join-us' },
      ]
    }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      // Prevent click-outside from closing mobile accordions
      if (window.innerWidth < 768) return;

      if (activeDropdown !== null &&
        dropdownRefs.current[activeDropdown] &&
        !dropdownRefs.current[activeDropdown].contains(event.target)) {
        setActiveDropdown(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  const toggleDropdown = (index) => {
    if (activeDropdown === index) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(index);
    }
  };

  // Function to determine dropdown position class
  const getDropdownPositionClass = (index) => {
    // If it's the last dropdown (Friends), align it to the right
    if (index === navLinks.length - 1) {
      return "right-0"; // Align to right edge of parent
    } else if (index === navLinks.length - 2) {
      return "right-0"; // Also align Account dropdown to right 
    }
    return "left-0"; // Default alignment for other dropdowns
  };

  // Icons for dropdown items
  const getIconForDropdownItem = (title) => {
    switch (title.toLowerCase()) {
      case 'environment':
        return <Globe size={16} className="mr-2" />;
      case 'tourism':
        return <Briefcase size={16} className="mr-2" />;
      case 'entertainment':
        return <Film size={16} className="mr-2" />;
      case 'energy':
        return <Zap size={16} className="mr-2" />;
      case 'education':
        return <GraduationCap size={16} className="mr-2" />;
      case 'events':
        return <Calendar size={16} className="mr-2" />;
      case 'blogs':
        return <Globe size={16} className="mr-2" />;
      case 'strengthen our journey':
        return <Users size={16} className="mr-2" />;
      case 'agri-tech':
        return <Leaf size={16} className="mr-2" />;
      default:
        return null;
    }
  };

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="container mx-auto px-4 py-2 flex justify-between items-center">
        <div className="flex items-center">
          {/* Logo placeholder */}
          <div className="flex items-center">
            <Link to="/">
              <img src={logo1} alt="Indo-Euro Synergy" className="h-20 w-auto cursor-pointer" />
            </Link>
          </div>
        </div>

        {/* Desktop Navigation with Dropdowns */}
        <nav className="hidden md:flex space-x-8 relative">
          {navLinks.map((link, index) => (
            <div
              key={index}
              className="relative group h-full flex items-center"
              ref={el => dropdownRefs.current[index] = el}
              onMouseEnter={() => setActiveDropdown(index)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <div className="flex items-center text-gray-700 hover:text-blue-600 font-medium py-2">
                <Link
                  to={link.href}
                  className="flex items-center"
                  onClick={() => setActiveDropdown(null)}
                >
                  {link.icon}
                  {link.title}
                </Link>
                <button
                  className="ml-1 focus:outline-none"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDropdown(index);
                  }}
                  aria-label="Toggle dropdown"
                >
                  <ChevronDown className={`transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} size={16} />
                </button>
              </div>

              {activeDropdown === index && (
                <div
                  className={`absolute top-full ${getDropdownPositionClass(index)} pt-2 w-56 z-20`}
                >
                  <div className="bg-white shadow-xl rounded-lg py-2 border border-gray-100">
                    {link.dropdownItems.map((item, itemIndex) => (
                      <Link
                        key={itemIndex}
                        to={item.href}
                        onClick={() => setActiveDropdown(null)}
                        className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center transition-colors"
                      >
                        {getIconForDropdownItem(item.title)}
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Navigation with Accordions */}
      {isMenuOpen && (
        <div className="md:hidden bg-white py-2 px-4 shadow-lg overflow-y-auto max-h-[85vh]">
          {navLinks.map((link, index) => (
            <div key={index} className="border-b border-gray-100 last:border-b-0">
              <div className="flex justify-between items-center w-full">
                <Link
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex-grow py-3 text-gray-700 hover:text-blue-600 flex items-center font-medium"
                >
                  {link.icon}
                  {link.title}
                </Link>
                <button
                  onClick={() => toggleDropdown(index)}
                  className="p-3 text-gray-700 hover:bg-gray-50 rounded-full"
                  aria-label="Toggle dropdown"
                >
                  <ChevronDown
                    className={`transform transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`}
                    size={20}
                  />
                </button>
              </div>

              {activeDropdown === index && (
                <div className="pl-4 pb-2 bg-gray-50 rounded-lg mb-2">
                  {link.dropdownItems.map((item, itemIndex) => (
                    <Link
                      key={itemIndex}
                      to={item.href}
                      onClick={() => {
                        setActiveDropdown(null);
                        setIsMenuOpen(false);
                      }}
                      className="block py-3 px-2 text-gray-600 hover:text-blue-600 flex items-center"
                    >
                      {getIconForDropdownItem(item.title)}
                      {item.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </header>
  );
}

export { Header };